import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { MobileApiService } from '@/services/mobileApiService';
import { MOBILE_APP_VERSION } from '@/mobile/constants/mobile';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { isBiometricLoginEnabled } from '@/utils/biometric';
import {
  handleNotificationNavigation,
  type PushNotificationPayload,
} from '@/mobile/services/notificationRouter';
import type { NavigateFunction } from 'react-router-dom';

export type PushInitOptions = {
  onToken?: (token: string) => void;
  onNotification?: (data: unknown) => void;
  onAction?: (data: unknown) => void;
  navigate?: NavigateFunction;
};

let initialized = false;
let lastDeviceId: string | null = null;
let lastPushToken: string | null = null;

async function resolveDeviceId(): Promise<string> {
  try {
    const id = await Device.getId();
    return id.identifier || `web-${Date.now()}`;
  } catch {
    return `device-${Date.now()}`;
  }
}

/**
 * Creates standard Android Notification Channels on startup.
 */
async function createNotificationChannels(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await PushNotifications.createChannel({
      id: 'vyora_emergency',
      name: 'Emergency & Security Alerts',
      description: 'Critical safety broadcasts and security alerts',
      importance: 5,
      visibility: 1,
      sound: 'default',
      vibration: true,
      lights: true,
      lightColor: '#DC2626',
    });

    await PushNotifications.createChannel({
      id: 'vyora_important',
      name: 'Important Society Notices',
      description: 'Notices, meetings, maintenance circulars, and invoices',
      importance: 4,
      visibility: 1,
      sound: 'default',
      vibration: true,
      lights: true,
      lightColor: '#0F766E',
    });

    await PushNotifications.createChannel({
      id: 'vyora_general',
      name: 'General Updates',
      description: 'Discussion forums, complaint updates, and visitor passes',
      importance: 3,
      visibility: 0,
      sound: 'default',
      vibration: false,
    });
  } catch (err) {
    console.warn('[push] Channel creation skipped/not supported', err);
  }
}

/**
 * Registers device with backend server only when an authenticated user session exists.
 */
export async function registerDeviceWithBackend(pushToken?: string | null): Promise<void> {
  const { authState, accessToken } = useAuthStore.getState();
  if (authState !== 'AUTHENTICATED' || !accessToken) {
    if (pushToken) lastPushToken = pushToken;
    return;
  }

  try {
    const info = await Device.getInfo();
    const deviceId = await resolveDeviceId();
    lastDeviceId = deviceId;
    if (pushToken) lastPushToken = pushToken;
    const tokenToRegister = pushToken ?? lastPushToken ?? null;
    const biometricEnabled = await isBiometricLoginEnabled().catch(() => false);

    await MobileApiService.registerDevice({
      platform:
        info.platform === 'ios' ? 'ios' : info.platform === 'android' ? 'android' : 'web',
      deviceId,
      model: info.model,
      osVersion: info.osVersion,
      appVersion: MOBILE_APP_VERSION,
      pushToken: tokenToRegister,
      fcmToken: tokenToRegister,
      biometricEnabled,
    });
  } catch (error) {
    console.warn('[push] device register failed', error);
  }
}

/**
 * Unregisters the current device on user logout to prevent cross-account notifications.
 */
export async function unregisterDeviceFromBackend(): Promise<void> {
  if (!lastDeviceId) return;
  try {
    await MobileApiService.unregisterDevice(lastDeviceId);
    lastPushToken = null;
  } catch (error) {
    console.warn('[push] device unregister failed', error);
  }
}

/** Enhanced push init — creates channels and sets up listeners. */
export async function initPushService(options: PushInitOptions = {}): Promise<void> {
  if (!Capacitor.isNativePlatform() || initialized) return;
  initialized = true;

  try {
    await createNotificationChannels();

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      void registerDeviceWithBackend(null);
      return;
    }

    // Register listeners BEFORE calling register()
    await PushNotifications.addListener('registration', (token) => {
      console.info('[push] device token received', token.value);
      lastPushToken = token.value;
      options.onToken?.(token.value);
      void registerDeviceWithBackend(token.value);
    });

    await PushNotifications.addListener('registrationError', (error) => {
      console.warn('[push] registration error', error?.error || error);
      void registerDeviceWithBackend(null);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.info('[push] foreground push received', notification);
      void Haptics.impact({ style: ImpactStyle.Light }).catch(() => undefined);

      const title = notification.title || 'New Notification';
      const body = notification.body || '';
      useUiStore.getState().showToast(`${title}: ${body}`, 'info');

      options.onNotification?.(notification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.info('[push] push notification tapped', action);
      options.onAction?.(action);

      const payload = action.notification?.data as PushNotificationPayload | undefined;
      if (options.navigate && payload) {
        const { authState, accessToken } = useAuthStore.getState();
        const isAuthenticated = authState === 'AUTHENTICATED' && Boolean(accessToken);
        handleNotificationNavigation(payload, options.navigate, isAuthenticated);
      }
    });

    try {
      await PushNotifications.register();
    } catch (regErr) {
      console.warn('[push] PushNotifications.register call failed', regErr);
    }
  } catch (error) {
    console.warn('[push] init skipped or failed', error);
    initialized = false;
  }
}

export function getLastRegisteredDeviceId(): string | null {
  return lastDeviceId;
}

export async function getDeliveredNotifications() {
  if (!Capacitor.isNativePlatform()) return [];
  try {
    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  } catch {
    return [];
  }
}
